export type InfraAssetEnvironment = 'dev' | 'test' | 'beta' | 'prod' | 'prod-beta' | 'shared'

export type InfraAssetKind =
  | 'dedicated-vm'
  | 'kubernetes-service'
  | 'node-group'
  | 'registry'
  | 'secrets'
  | 'managed-data'

export type InfraAssetCriticality = 'critical' | 'high' | 'medium'

export interface InfraAssetSourceRef {
  label: string
  path: string
}

export interface InfraAssetProbe {
  label: string
  kind: 'http' | 'tcp' | 'udp' | 'dns' | 'tls' | 'kubernetes' | 'promql'
  target: string
  expectation: string
}

export interface InfraAssetTelemetrySignal {
  label: string
  state: 'available' | 'planned' | 'gap'
  source: string
  description: string
}

export interface InfraAssetAnalysisQuery {
  label: string
  kind: 'promql' | 'logql' | 'upctl' | 'kubectl'
  query: string
  intent: string
}

export interface InfraAssetHealthMetric {
  id: string
  label: string
  unit: string
  category: 'availability' | 'cpu' | 'memory' | 'disk' | 'network' | 'restarts' | 'errors'
  query: string
  description: string
  emptyState: string
}

export interface InfraOpsAsset {
  id: string
  name: string
  environment: InfraAssetEnvironment
  kind: InfraAssetKind
  criticality: InfraAssetCriticality
  description: string
  owner: string
  runtime: string
  endpoint?: string
  upcloudUuid?: string
  upcloudHostname?: string
  createdAt?: string
  dependencies: string[]
  monitorSignals: string[]
  operatorQuestions: string[]
  firstResponse: string[]
  failureModes: string[]
  probes: InfraAssetProbe[]
  telemetry: InfraAssetTelemetrySignal[]
  analysisQueries: InfraAssetAnalysisQuery[]
  healthMetrics: InfraAssetHealthMetric[]
  sourceRefs: InfraAssetSourceRef[]
  promql?: string
  healthyWhen?: 'positive' | 'zero'
}

function vmSelector(assetId: string, hostname: string): string {
  return `agion_asset_id="${assetId}",host_name="${hostname}"`
}

function dedicatedVmHealthMetrics(assetId: string, hostname: string): InfraAssetHealthMetric[] {
  const selector = vmSelector(assetId, hostname)
  return [
    {
      id: 'vm-cpu',
      label: 'CPU usage',
      unit: '%',
      category: 'cpu',
      query: `100 * (1 - avg(rate(node_cpu_seconds_total{${selector},mode="idle"}[5m])))`,
      description: 'Host CPU saturation from node exporter style metrics shipped by agion-alloy.',
      emptyState: 'No VM CPU series yet. Install agion-alloy on this standalone VM.',
    },
    {
      id: 'vm-memory',
      label: 'RAM used',
      unit: '%',
      category: 'memory',
      query: `100 * (1 - (node_memory_MemAvailable_bytes{${selector}} / node_memory_MemTotal_bytes{${selector}}))`,
      description: 'Host memory pressure using available memory over total RAM.',
      emptyState: 'No VM RAM series yet. Install agion-alloy host metrics.',
    },
    {
      id: 'vm-ram-size',
      label: 'RAM size',
      unit: 'GiB',
      category: 'memory',
      query: `node_memory_MemTotal_bytes{${selector}} / 1024 / 1024 / 1024`,
      description: 'Provisioned RAM visible to the host.',
      emptyState: 'No RAM size series yet. Host metrics are not being collected.',
    },
    {
      id: 'vm-root-disk',
      label: 'Root disk used',
      unit: '%',
      category: 'disk',
      query: `100 * (1 - (node_filesystem_avail_bytes{${selector},mountpoint="/",fstype!~"tmpfs|overlay"} / node_filesystem_size_bytes{${selector},mountpoint="/",fstype!~"tmpfs|overlay"}))`,
      description: 'Root filesystem saturation. This is often the fastest way to explain partial service failure.',
      emptyState: 'No VM disk series yet. Host filesystem metrics are not being collected.',
    },
    {
      id: 'vm-network-rx',
      label: 'Network receive',
      unit: 'MiB/s',
      category: 'network',
      query: `sum(rate(node_network_receive_bytes_total{${selector},device!~"lo|docker.*|veth.*"}[5m])) / 1024 / 1024`,
      description: 'Inbound network throughput across non-loopback interfaces.',
      emptyState: 'No VM network receive series yet. Host network metrics are not being collected.',
    },
    {
      id: 'vm-network-tx',
      label: 'Network transmit',
      unit: 'MiB/s',
      category: 'network',
      query: `sum(rate(node_network_transmit_bytes_total{${selector},device!~"lo|docker.*|veth.*"}[5m])) / 1024 / 1024`,
      description: 'Outbound network throughput across non-loopback interfaces.',
      emptyState: 'No VM network transmit series yet. Host network metrics are not being collected.',
    },
    {
      id: 'vm-oom-kills',
      label: 'OOM kills',
      unit: 'events',
      category: 'restarts',
      query: `increase(node_vmstat_oom_kill{${selector}}[1h])`,
      description: 'Kernel OOM kills over the selected window.',
      emptyState: 'No VM OOM series yet. Host vmstat metrics are not being collected.',
    },
  ]
}

function workloadNodeHealthMetrics(environment: 'prod-beta' | 'test'): InfraAssetHealthMetric[] {
  const namespaceMatcher = environment === 'prod-beta' ? '(prod|beta)-.*' : '(dev|test)-.*'
  const nodePoolSelector = 'kube_node_labels{label_agion_dev_node_pool="workloads"}'
  return [
    {
      id: 'node-ready',
      label: 'Ready workload nodes',
      unit: 'nodes',
      category: 'availability',
      query: `sum(kube_node_status_condition{condition="Ready",status="true"} * on(node) group_left(label_agion_dev_node_pool) ${nodePoolSelector})`,
      description: 'How many workload nodes are Ready and schedulable.',
      emptyState: 'No workload node readiness series found.',
    },
    {
      id: 'node-cpu',
      label: 'Workload CPU',
      unit: 'cores',
      category: 'cpu',
      query: `sum(rate(container_cpu_usage_seconds_total{namespace=~"${namespaceMatcher}",container!="",image!=""}[5m]))`,
      description: 'CPU consumed by application containers in the environment workload namespaces.',
      emptyState: 'No workload CPU series found for this environment.',
    },
    {
      id: 'node-memory',
      label: 'Workload RAM',
      unit: 'GiB',
      category: 'memory',
      query: `sum(container_memory_working_set_bytes{namespace=~"${namespaceMatcher}",container!="",image!=""}) / 1024 / 1024 / 1024`,
      description: 'Working-set memory consumed by workload containers.',
      emptyState: 'No workload memory series found for this environment.',
    },
    {
      id: 'node-disk-pressure',
      label: 'Disk pressure nodes',
      unit: 'nodes',
      category: 'disk',
      query: `sum(kube_node_status_condition{condition="DiskPressure",status="true"} * on(node) group_left(label_agion_dev_node_pool) ${nodePoolSelector})`,
      description: 'Workload nodes actively reporting disk pressure.',
      emptyState: 'No node disk pressure condition series found.',
    },
    {
      id: 'node-oom',
      label: 'OOMKilled containers',
      unit: 'containers',
      category: 'restarts',
      query: `sum(kube_pod_container_status_last_terminated_reason{namespace=~"${namespaceMatcher}",reason="OOMKilled"})`,
      description: 'Containers whose last termination reason was OOMKilled.',
      emptyState: 'No OOMKilled termination series found.',
    },
  ]
}

function namespaceHealthMetrics(assetId: string, namespaceRegex: string): InfraAssetHealthMetric[] {
  return [
    {
      id: `${assetId}-pod-ready`,
      label: 'Ready pods',
      unit: 'pods',
      category: 'availability',
      query: `sum(kube_pod_status_ready{namespace=~"${namespaceRegex}",condition="true"})`,
      description: 'Pods reporting Ready in the owning namespaces.',
      emptyState: 'No pod readiness series found.',
    },
    {
      id: `${assetId}-cpu`,
      label: 'CPU usage',
      unit: 'cores',
      category: 'cpu',
      query: `sum(rate(container_cpu_usage_seconds_total{namespace=~"${namespaceRegex}",container!="",image!=""}[5m]))`,
      description: 'Container CPU used by this asset namespace set.',
      emptyState: 'No container CPU series found.',
    },
    {
      id: `${assetId}-memory`,
      label: 'RAM usage',
      unit: 'GiB',
      category: 'memory',
      query: `sum(container_memory_working_set_bytes{namespace=~"${namespaceRegex}",container!="",image!=""}) / 1024 / 1024 / 1024`,
      description: 'Container working-set memory used by this asset namespace set.',
      emptyState: 'No container memory series found.',
    },
    {
      id: `${assetId}-restarts`,
      label: 'Restarts',
      unit: 'events',
      category: 'restarts',
      query: `sum(increase(kube_pod_container_status_restarts_total{namespace=~"${namespaceRegex}"}[1h]))`,
      description: 'Container restarts over the last hour.',
      emptyState: 'No restart series found.',
    },
    {
      id: `${assetId}-oom`,
      label: 'OOMKilled containers',
      unit: 'containers',
      category: 'restarts',
      query: `sum(kube_pod_container_status_last_terminated_reason{namespace=~"${namespaceRegex}",reason="OOMKilled"})`,
      description: 'Containers whose last termination reason was OOMKilled.',
      emptyState: 'No OOMKilled termination series found.',
    },
  ]
}

export const infraOpsAssets: InfraOpsAsset[] = [
  {
    id: 'livekit-prod',
    name: 'Production LiveKit SFU',
    environment: 'prod-beta',
    kind: 'dedicated-vm',
    criticality: 'high',
    description: 'Dedicated UpCloud VM for LiveKit WebRTC signaling, media relay, and TURN used by production and beta voice workflows.',
    owner: 'UpCloud Terraform livekit-server module',
    runtime: 'UpCloud VM with nginx, certbot, LiveKit, TURN, and floating IP',
    endpoint: 'live.agion.cloud',
    upcloudUuid: '00315262-ee44-45fb-91f5-4e475b3e5636',
    upcloudHostname: 'agion-production-livekit',
    createdAt: '2026-02-11 18:25:03 EET',
    dependencies: ['UpCloud public networking', 'Lets Encrypt certificate', 'LiveKit API credentials', 'production private network'],
    monitorSignals: ['VM state', 'HTTPS/WSS reachability', 'TURN UDP 443', 'LiveKit systemd health', 'nginx 5xx rate'],
    operatorQuestions: [
      'Can clients establish WebRTC rooms through the public LiveKit endpoint?',
      'Is the VM online, reachable, and serving the certificate for live.agion.cloud?',
      'Are LiveKit, nginx, TURN, and certbot healthy on the same host?',
      'Is this a platform outage or only media-plane degradation?',
    ],
    firstResponse: [
      'Check black-box HTTPS, WSS, TLS, and TURN probes before SSHing to the VM.',
      'If probes fail, verify UpCloud server state and floating IP assignment.',
      'Inspect livekit and nginx journald logs for startup, auth, TLS, and upstream errors.',
      'Confirm production and beta services still point to the expected LiveKit endpoint and API credentials.',
    ],
    failureModes: [
      'Expired or missing Lets Encrypt certificate breaks WSS and TURN TLS.',
      'LiveKit process down while nginx still answers HTTP.',
      'TURN UDP/TCP path blocked by firewall or floating IP routing.',
      'API key mismatch causes rooms to fail even when endpoint is reachable.',
      'Host disk pressure or unattended upgrade leaves services partially restarted.',
    ],
    probes: [
      { label: 'HTTPS landing path', kind: 'http', target: 'https://live.agion.cloud/', expectation: '2xx/3xx response with valid certificate' },
      { label: 'WebSocket upgrade path', kind: 'tcp', target: 'live.agion.cloud:443', expectation: 'TCP connect succeeds from platform probe location' },
      { label: 'TURN relay path', kind: 'udp', target: 'live.agion.cloud:443/udp', expectation: 'TURN allocation path is reachable where probe support exists' },
      { label: 'TLS expiry', kind: 'tls', target: 'live.agion.cloud:443', expectation: 'certificate chain valid and expiry above alert threshold' },
    ],
    telemetry: [
      { label: 'Endpoint probes', state: 'planned', source: 'blackbox exporter', description: 'Synthetic availability from inside the platform monitoring plane.' },
      { label: 'Host logs', state: 'gap', source: 'agion-alloy on VM', description: 'Needed for livekit, nginx, certbot, cloud-init, and systemd journal evidence.' },
      { label: 'Host saturation', state: 'gap', source: 'agion-alloy host metrics', description: 'Needed for CPU, memory, disk, filesystem, and network saturation.' },
      { label: 'UpCloud identity', state: 'available', source: 'Terraform and upctl', description: 'Server UUID, hostname, creation date, and module ownership are known.' },
    ],
    analysisQueries: [
      { label: 'LiveKit scrape presence', kind: 'promql', query: 'max(up{job=~".*livekit.*"})', intent: 'Shows whether a LiveKit metrics target exists and is up.' },
      { label: 'Host logs once Alloy is installed', kind: 'logql', query: '{agion_asset_id="livekit-prod"} |= "error"', intent: 'Find host-level LiveKit/nginx/certbot failures after VM log collection exists.' },
      { label: 'UpCloud server state', kind: 'upctl', query: 'upctl server show 00315262-ee44-45fb-91f5-4e475b3e5636', intent: 'Verify VM state, network, plan, zone, and attached storage.' },
    ],
    healthMetrics: dedicatedVmHealthMetrics('livekit-prod', 'agion-production-livekit'),
    sourceRefs: [
      { label: 'Terraform production livekit module', path: 'Agion-UpCloud-Infra/environments/production/main.tf' },
      { label: 'LiveKit server module', path: 'Agion-UpCloud-Infra/modules/livekit-server/main.tf' },
    ],
    promql: 'max(up{job=~".*livekit.*"})',
    healthyWhen: 'positive',
  },
  {
    id: 'livekit-test',
    name: 'Test LiveKit SFU',
    environment: 'test',
    kind: 'dedicated-vm',
    criticality: 'medium',
    description: 'Dedicated UpCloud VM for the test LiveKit endpoint and TURN path.',
    owner: 'UpCloud Terraform livekit-server module',
    runtime: 'UpCloud VM with nginx, certbot, LiveKit, TURN, and floating IP',
    endpoint: 'live.test.agion.dev',
    upcloudHostname: 'agion-livekit',
    dependencies: ['UpCloud public networking', 'Lets Encrypt certificate', 'LiveKit API credentials', 'test private network'],
    monitorSignals: ['VM state', 'HTTPS/WSS reachability', 'TURN UDP 443', 'LiveKit systemd health', 'nginx 5xx rate'],
    operatorQuestions: [
      'Does the test media path behave like production before changes graduate?',
      'Is the endpoint reachable from the same network perspective as test users and services?',
      'Is a test LiveKit failure isolated from production?',
    ],
    firstResponse: [
      'Check HTTPS, WSS, TLS, and TURN probes for live.test.agion.dev.',
      'Compare test LiveKit failures against current test GitOps and Terraform changes.',
      'Inspect LiveKit/nginx host logs once VM Alloy collection is installed.',
    ],
    failureModes: [
      'Certificate issuance or renewal failed during cloud-init/bootstrap.',
      'Firewall allows HTTPS but not TURN relay traffic.',
      'LiveKit binary or config drift from production.',
      'Test endpoint DNS points to stale floating IP.',
    ],
    probes: [
      { label: 'HTTPS landing path', kind: 'http', target: 'https://live.test.agion.dev/', expectation: '2xx/3xx response with valid certificate' },
      { label: 'WebSocket upgrade path', kind: 'tcp', target: 'live.test.agion.dev:443', expectation: 'TCP connect succeeds from platform probe location' },
      { label: 'TURN relay path', kind: 'udp', target: 'live.test.agion.dev:443/udp', expectation: 'TURN allocation path is reachable where probe support exists' },
      { label: 'TLS expiry', kind: 'tls', target: 'live.test.agion.dev:443', expectation: 'certificate chain valid and expiry above alert threshold' },
    ],
    telemetry: [
      { label: 'Endpoint probes', state: 'planned', source: 'blackbox exporter', description: 'Synthetic availability for the test media endpoint.' },
      { label: 'Host logs', state: 'gap', source: 'agion-alloy on VM', description: 'Needed for systemd, LiveKit, nginx, certbot, and bootstrap evidence.' },
      { label: 'Terraform ownership', state: 'available', source: 'Agion-UpCloud-Infra', description: 'The test LiveKit module and endpoint are defined in Terraform.' },
    ],
    analysisQueries: [
      { label: 'LiveKit scrape presence', kind: 'promql', query: 'max(up{job=~".*livekit.*"})', intent: 'Shows whether a LiveKit metrics target exists and is up.' },
      { label: 'Host logs once Alloy is installed', kind: 'logql', query: '{agion_asset_id="livekit-test"} |= "error"', intent: 'Find host-level failures after VM log collection exists.' },
      { label: 'Terraform source', kind: 'upctl', query: 'upctl server list --output json | jq \'.[] | select(.hostname=="agion-livekit")\'', intent: 'Locate the concrete UpCloud server backing the test endpoint.' },
    ],
    healthMetrics: dedicatedVmHealthMetrics('livekit-test', 'agion-livekit'),
    sourceRefs: [
      { label: 'Terraform test livekit module', path: 'Agion-UpCloud-Infra/environments/test/livekit.tf' },
      { label: 'LiveKit server module', path: 'Agion-UpCloud-Infra/modules/livekit-server/main.tf' },
    ],
    promql: 'max(up{job=~".*livekit.*"})',
    healthyWhen: 'positive',
  },
  {
    id: 'prod-workloads-node-group',
    name: 'Production UKS workload node group',
    environment: 'prod-beta',
    kind: 'node-group',
    criticality: 'critical',
    description: 'UpCloud Kubernetes worker node group that runs Agion production and beta application workloads.',
    owner: 'UpCloud Terraform UKS module',
    runtime: 'UKS node group agion-workloads, 4 nodes, 4xCPU-8GB, workload=agion NoSchedule taint',
    upcloudUuid: '00547308-0c64-4635-84ff-47870cda3ad7',
    upcloudHostname: 'agion-workloads-klfbm-zcfrp',
    createdAt: '2026-02-12 00:21:18 EET',
    dependencies: ['agion-production-k8s control plane', 'production private network', 'Cilium pod network', 'container registry pull path'],
    monitorSignals: ['Node readiness', 'node filesystem pressure', 'pod scheduling failures', 'image pull failures', 'workload taint coverage'],
    operatorQuestions: [
      'Do production and beta workloads have enough ready schedulable capacity?',
      'Are node failures isolated to one UpCloud host or the node group as a whole?',
      'Are pods blocked by taints, pressure, image pulls, or network issues?',
    ],
    firstResponse: [
      'Check ready workload node count and compare it with expected Terraform node count.',
      'Inspect pods pending or failing on workload nodes before touching nodes.',
      'Check image pull failures, node pressure conditions, and Cilium health.',
      'Escalate as capacity incident if ready nodes drop below workload quorum.',
    ],
    failureModes: [
      'A worker VM is online in UpCloud but NotReady in Kubernetes.',
      'Disk or memory pressure blocks scheduling.',
      'Registry or pull-secret failure presents as node workload outage.',
      'Cilium or private-network disruption prevents pod networking.',
      'Taint/label drift makes nodes invisible to application workloads.',
    ],
    probes: [
      { label: 'Ready workload nodes', kind: 'promql', target: 'kube_node_status_condition{condition="Ready",status="true"}', expectation: 'Expected production workload node count stays ready.' },
      { label: 'Scheduling pressure', kind: 'promql', target: 'kube_pod_status_phase{phase="Pending"}', expectation: 'No sustained production workload pending backlog.' },
      { label: 'Image pull path', kind: 'promql', target: 'kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"}', expectation: 'No sustained pull failures on workload namespaces.' },
    ],
    telemetry: [
      { label: 'Kubernetes node state', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Readiness, labels, taints, and scheduling state are Kubernetes-native.' },
      { label: 'Pod scheduling symptoms', state: 'available', source: 'Mimir kube metrics', description: 'Pending pods and waiting reasons expose most workload-facing failures.' },
      { label: 'UpCloud VM state', state: 'planned', source: 'future UpCloud exporter', description: 'Useful for correlating NotReady nodes with provider-level VM state.' },
    ],
    analysisQueries: [
      { label: 'Ready workload nodes', kind: 'promql', query: 'count(kube_node_status_condition{condition="Ready",status="true"} * on(node) group_left(label_agion_dev_node_pool) kube_node_labels{label_agion_dev_node_pool="workloads"})', intent: 'Counts ready workload nodes.' },
      { label: 'Node pressure', kind: 'promql', query: 'kube_node_status_condition{condition=~"DiskPressure|MemoryPressure|PIDPressure|NetworkUnavailable",status="true"}', intent: 'Shows active node pressure conditions.' },
      { label: 'Workload node inventory', kind: 'kubectl', query: 'kubectl get nodes -l agion.dev/node-pool=workloads -o wide', intent: 'Lists workload nodes, versions, addresses, and readiness.' },
    ],
    healthMetrics: workloadNodeHealthMetrics('prod-beta'),
    sourceRefs: [
      { label: 'Terraform production UKS node groups', path: 'Agion-UpCloud-Infra/environments/production/main.tf' },
      { label: 'UKS Terraform module', path: 'Agion-UpCloud-Infra/modules/uks/main.tf' },
    ],
    promql: 'count(kube_node_labels{label_agion_dev_node_pool="workloads"})',
    healthyWhen: 'positive',
  },
  {
    id: 'test-workloads-node-group',
    name: 'Test UKS workload node group',
    environment: 'test',
    kind: 'node-group',
    criticality: 'high',
    description: 'UpCloud Kubernetes worker node group that runs dev and test Agion application workloads.',
    owner: 'UpCloud Terraform UKS module',
    runtime: 'UKS node group agion-workloads, 3 nodes, 4xCPU-8GB, workload=agion NoSchedule taint',
    dependencies: ['agion-test-k8s control plane', 'test private network', 'Cilium pod network', 'container registry pull path'],
    monitorSignals: ['Node readiness', 'node filesystem pressure', 'pod scheduling failures', 'image pull failures', 'workload taint coverage'],
    operatorQuestions: [
      'Is test capacity healthy enough to validate changes before promotion?',
      'Are failures caused by capacity, registry pulls, or test application rollout state?',
      'Is the node group drifting from Terraform expectations?',
    ],
    firstResponse: [
      'Check ready workload node count against expected test capacity.',
      'Inspect Pending pods and ImagePullBackOff before restarting workloads.',
      'Confirm the test node labels and taints still match GitOps scheduling assumptions.',
    ],
    failureModes: [
      'Test nodes become NotReady after upgrades or provider maintenance.',
      'Registry pull path breaks and presents as broad test workload failure.',
      'Node taints or labels drift from scheduler expectations.',
      'Disk pressure from test workloads blocks pods.',
    ],
    probes: [
      { label: 'Ready workload nodes', kind: 'promql', target: 'kube_node_status_condition{condition="Ready",status="true"}', expectation: 'Expected test workload node count stays ready.' },
      { label: 'Scheduling pressure', kind: 'promql', target: 'kube_pod_status_phase{phase="Pending"}', expectation: 'No sustained test workload pending backlog.' },
      { label: 'Image pull path', kind: 'promql', target: 'kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"}', expectation: 'No sustained pull failures on test workloads.' },
    ],
    telemetry: [
      { label: 'Kubernetes node state', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Readiness, labels, taints, and scheduling state are Kubernetes-native.' },
      { label: 'Pod scheduling symptoms', state: 'available', source: 'Mimir kube metrics', description: 'Pending pods and waiting reasons expose most workload-facing failures.' },
      { label: 'UpCloud VM state', state: 'planned', source: 'future UpCloud exporter', description: 'Useful later for provider-level correlation.' },
    ],
    analysisQueries: [
      { label: 'Ready workload nodes', kind: 'promql', query: 'count(kube_node_status_condition{condition="Ready",status="true"} * on(node) group_left(label_agion_dev_node_pool) kube_node_labels{label_agion_dev_node_pool="workloads"})', intent: 'Counts ready workload nodes.' },
      { label: 'Node pressure', kind: 'promql', query: 'kube_node_status_condition{condition=~"DiskPressure|MemoryPressure|PIDPressure|NetworkUnavailable",status="true"}', intent: 'Shows active node pressure conditions.' },
      { label: 'Workload node inventory', kind: 'kubectl', query: 'kubectl get nodes -l agion.dev/node-pool=workloads -o wide', intent: 'Lists workload nodes, versions, addresses, and readiness.' },
    ],
    healthMetrics: workloadNodeHealthMetrics('test'),
    sourceRefs: [
      { label: 'Terraform test UKS node groups', path: 'Agion-UpCloud-Infra/environments/test/main.tf' },
      { label: 'UKS Terraform module', path: 'Agion-UpCloud-Infra/modules/uks/main.tf' },
    ],
    promql: 'count(kube_node_labels{label_agion_dev_node_pool="workloads"})',
    healthyWhen: 'positive',
  },
  {
    id: 'harbor-test',
    name: 'Test Harbor registry',
    environment: 'test',
    kind: 'registry',
    criticality: 'critical',
    description: 'Harbor registry and image pull path for test/dev platform images and edge pull secrets.',
    owner: 'GitOps Harbor application plus UpCloud object storage and PostgreSQL',
    runtime: 'Kubernetes Harbor release backed by PostgreSQL and harbor-images object storage',
    endpoint: 'harbor.test.agion.dev',
    dependencies: ['test PostgreSQL harbor database', 'harbor-images object storage bucket', 'APISIX route', 'OpenBao pull credentials'],
    monitorSignals: ['Harbor core/registry pod readiness', 'registry 5xx rate', 'image pull errors', 'object storage access', 'certificate validity'],
    operatorQuestions: [
      'Can test/dev clusters pull and push images through Harbor?',
      'Are failures in Harbor itself, APISIX/TLS routing, object storage, database, or pull secrets?',
      'Are image pull errors spreading across workloads?',
    ],
    firstResponse: [
      'Check Harbor pod readiness and APISIX route health.',
      'Check ImagePullBackOff across test/dev namespaces.',
      'Verify object storage and PostgreSQL dependencies before redeploying Harbor.',
      'Inspect ExternalSecret sync state for Harbor pull credentials.',
    ],
    failureModes: [
      'Harbor core is up but registry storage backend is unavailable.',
      'APISIX or TLS route fails while pods remain healthy.',
      'Pull secret expired or missing from workload namespace.',
      'PostgreSQL dependency blocks Harbor core operations.',
    ],
    probes: [
      { label: 'Harbor HTTPS', kind: 'http', target: 'https://harbor.test.agion.dev/', expectation: 'Harbor responds through APISIX with valid TLS.' },
      { label: 'Registry API', kind: 'http', target: 'https://harbor.test.agion.dev/v2/', expectation: 'Registry API responds with expected auth challenge or success.' },
      { label: 'Image pull symptom', kind: 'promql', target: 'ImagePullBackOff waiting reason', expectation: 'No sustained pull failures.' },
    ],
    telemetry: [
      { label: 'Kubernetes pod state', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Pod readiness and restart state are available.' },
      { label: 'Pull failure symptoms', state: 'available', source: 'Mimir kube metrics', description: 'ImagePullBackOff is the user-visible registry failure symptom.' },
      { label: 'External endpoint probe', state: 'planned', source: 'blackbox exporter', description: 'Needed to catch route/TLS failures when pods look healthy.' },
    ],
    analysisQueries: [
      { label: 'Harbor up target', kind: 'promql', query: 'max(up{namespace=~".*harbor.*"} or up{job=~".*harbor.*"})', intent: 'Shows whether Harbor scrape targets are up.' },
      { label: 'Image pull failures', kind: 'promql', query: 'sum by (namespace,pod) (kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"})', intent: 'Find workloads affected by registry/pull failures.' },
      { label: 'Harbor pods', kind: 'kubectl', query: 'kubectl get pods -n harbor -o wide', intent: 'Inspect Harbor pod readiness and node placement.' },
    ],
    healthMetrics: namespaceHealthMetrics('harbor-test', '.*harbor.*'),
    sourceRefs: [
      { label: 'GitOps test Harbor application', path: 'Agion-UpCloud-GitOps/argocd/applications/test-infrastructure/infra-harbor.yaml' },
      { label: 'Terraform test Harbor storage and DB', path: 'Agion-UpCloud-Infra/environments/test/main.tf' },
    ],
    promql: 'max(up{namespace=~".*harbor.*"} or up{job=~".*harbor.*"})',
    healthyWhen: 'positive',
  },
  {
    id: 'harbor-prod',
    name: 'Production Harbor registry',
    environment: 'prod',
    kind: 'registry',
    criticality: 'critical',
    description: 'Production Harbor registry backing Agion image publishing and runtime pulls.',
    owner: 'GitOps Harbor application plus dedicated UpCloud object storage and PostgreSQL',
    runtime: 'Kubernetes Harbor release backed by Production PostgreSQL and dedicated harbor-images object storage',
    endpoint: 'harbor.agion.cloud',
    dependencies: ['production PostgreSQL harbor database', 'dedicated harbor-images object storage bucket', 'APISIX route', 'OpenBao pull credentials'],
    monitorSignals: ['Harbor core/registry pod readiness', 'registry 5xx rate', 'image pull errors', 'object storage access', 'certificate validity'],
    operatorQuestions: [
      'Can production and beta workloads pull required images?',
      'Is this a publishing issue, pull issue, Harbor dependency issue, or routing/TLS issue?',
      'Are ArgoCD rollouts blocked by registry availability?',
    ],
    firstResponse: [
      'Check production Harbor pod readiness and APISIX route health.',
      'Check ImagePullBackOff across production and beta namespaces.',
      'Verify OpenBao-backed pull secrets and ExternalSecret sync status.',
      'Check Harbor PostgreSQL and object storage symptoms before restarting components.',
    ],
    failureModes: [
      'Production deployments fail because image pulls cannot authenticate.',
      'Harbor registry storage is unavailable while UI/core still responds.',
      'APISIX route or TLS certificate breaks external registry access.',
      'OpenBao or ExternalSecrets failure prevents pull-secret refresh.',
    ],
    probes: [
      { label: 'Harbor HTTPS', kind: 'http', target: 'https://harbor.agion.cloud/', expectation: 'Harbor responds through APISIX with valid TLS.' },
      { label: 'Registry API', kind: 'http', target: 'https://harbor.agion.cloud/v2/', expectation: 'Registry API responds with expected auth challenge or success.' },
      { label: 'Image pull symptom', kind: 'promql', target: 'ImagePullBackOff waiting reason', expectation: 'No sustained pull failures.' },
    ],
    telemetry: [
      { label: 'Kubernetes pod state', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Pod readiness and restart state are available.' },
      { label: 'Pull failure symptoms', state: 'available', source: 'Mimir kube metrics', description: 'ImagePullBackOff is the user-visible registry failure symptom.' },
      { label: 'External endpoint probe', state: 'planned', source: 'blackbox exporter', description: 'Needed to catch route/TLS failures when pods look healthy.' },
    ],
    analysisQueries: [
      { label: 'Harbor up target', kind: 'promql', query: 'max(up{namespace=~".*harbor.*"} or up{job=~".*harbor.*"})', intent: 'Shows whether Harbor scrape targets are up.' },
      { label: 'Image pull failures', kind: 'promql', query: 'sum by (namespace,pod) (kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"})', intent: 'Find workloads affected by registry/pull failures.' },
      { label: 'Harbor pods', kind: 'kubectl', query: 'kubectl get pods -n harbor -o wide', intent: 'Inspect Harbor pod readiness and node placement.' },
    ],
    healthMetrics: namespaceHealthMetrics('harbor-prod', '.*harbor.*'),
    sourceRefs: [
      { label: 'GitOps production Harbor application', path: 'Agion-UpCloud-GitOps/argocd/applications/prod-infrastructure/infra-harbor.yaml' },
      { label: 'Terraform production Harbor storage', path: 'Agion-UpCloud-Infra/environments/production/main.tf' },
    ],
    promql: 'max(up{namespace=~".*harbor.*"} or up{job=~".*harbor.*"})',
    healthyWhen: 'positive',
  },
  {
    id: 'registry-prod',
    name: 'Registry image pull endpoint',
    environment: 'shared',
    kind: 'registry',
    criticality: 'critical',
    description: 'Canonical image pull endpoint used by GitOps versions for Agion application and edge images.',
    owner: 'GitOps image promotion and registry routing',
    runtime: 'registry.agion.dev image names consumed by Kubernetes workloads and edge components',
    endpoint: 'registry.agion.dev',
    dependencies: ['Harbor registry availability', 'image promotion manifests', 'pull secrets in OpenBao', 'APISIX/TLS routing'],
    monitorSignals: ['ImagePullBackOff count', 'registry 5xx rate', 'manifest availability', 'pull-secret freshness'],
    operatorQuestions: [
      'Can clusters pull the exact promoted image tags they are trying to run?',
      'Are failures caused by missing manifests, registry auth, DNS/TLS, or Harbor backend health?',
      'Is the issue isolated to one environment or shared across promotion consumers?',
    ],
    firstResponse: [
      'Start with ImagePullBackOff counts and affected namespaces.',
      'Compare failing image tags against GitOps versions.',
      'Check pull-secret ExternalSecrets and OpenBao availability.',
      'Probe registry.agion.dev and Harbor /v2/ routing.',
    ],
    failureModes: [
      'GitOps references an image tag that was not pushed or promoted.',
      'Pull secret refresh failed because OpenBao or ExternalSecrets is degraded.',
      'Registry DNS/TLS route breaks while Harbor pods are healthy.',
      'Harbor storage backend fails manifest or layer retrieval.',
    ],
    probes: [
      { label: 'Registry HTTPS', kind: 'http', target: 'https://registry.agion.dev/v2/', expectation: 'Registry route responds with expected auth challenge or success.' },
      { label: 'Pull symptom', kind: 'promql', target: 'ImagePullBackOff waiting reason', expectation: 'No sustained pull failures by namespace.' },
      { label: 'Manifest availability', kind: 'http', target: 'registry.agion.dev/<image>:<tag>', expectation: 'Promoted runtime images exist before rollout.' },
    ],
    telemetry: [
      { label: 'Pull failure symptoms', state: 'available', source: 'Mimir kube metrics', description: 'ImagePullBackOff exposes broken image consumption.' },
      { label: 'Registry endpoint probe', state: 'planned', source: 'blackbox exporter', description: 'Needed to distinguish route/TLS from cluster pull-secret failures.' },
      { label: 'Promotion manifest state', state: 'available', source: 'GitOps apps/versions', description: 'Expected image tags are declared in Git.' },
    ],
    analysisQueries: [
      { label: 'Image pull failures', kind: 'promql', query: 'sum by (namespace,pod,container) (kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"})', intent: 'Find current consumers failing to pull images.' },
      { label: 'Waiting reasons', kind: 'promql', query: 'sum by (reason,namespace) (kube_pod_container_status_waiting_reason)', intent: 'Separate pull failures from other pod waiting states.' },
      { label: 'Current GitOps images', kind: 'kubectl', query: 'kubectl get pods -A -o jsonpath="{range .items[*]}{.metadata.namespace} {.metadata.name} {.spec.containers[*].image}{\\"\\n\\"}{end}"', intent: 'Compare running/requested images against promoted tags.' },
    ],
    healthMetrics: [
      {
        id: 'registry-image-pulls',
        label: 'ImagePullBackOff',
        unit: 'containers',
        category: 'errors',
        query: 'sum(kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"})',
        description: 'Current containers unable to pull images.',
        emptyState: 'No ImagePullBackOff series found.',
      },
      {
        id: 'registry-err-image-pulls',
        label: 'ErrImagePull',
        unit: 'containers',
        category: 'errors',
        query: 'sum(kube_pod_container_status_waiting_reason{reason="ErrImagePull"})',
        description: 'Current containers in ErrImagePull state.',
        emptyState: 'No ErrImagePull series found.',
      },
      {
        id: 'registry-harbor-ready',
        label: 'Harbor ready pods',
        unit: 'pods',
        category: 'availability',
        query: 'sum(kube_pod_status_ready{namespace=~".*harbor.*",condition="true"})',
        description: 'Ready Harbor pods backing registry.agion.dev.',
        emptyState: 'No Harbor pod readiness series found.',
      },
    ],
    sourceRefs: [
      { label: 'GitOps application image versions', path: 'Agion-UpCloud-GitOps/apps/versions' },
      { label: 'Harbor pull ExternalSecrets', path: 'Agion-UpCloud-GitOps/apps/base/infra-service/externalsecret-registry-pull.yaml' },
    ],
    promql: 'sum(kube_pod_container_status_waiting_reason{reason="ImagePullBackOff"})',
    healthyWhen: 'zero',
  },
  {
    id: 'openbao-dev-test',
    name: 'Dev/Test OpenBao',
    environment: 'test',
    kind: 'secrets',
    criticality: 'critical',
    description: 'OpenBao secret backends for dev and test ExternalSecrets, PKI, application credentials, and pull credentials.',
    owner: 'GitOps OpenBao Helm release and external-secrets ClusterSecretStores',
    runtime: 'Kubernetes services openbao.dev-openbao.svc.cluster.local and openbao.test-openbao.svc.cluster.local',
    dependencies: ['OpenBao pod readiness', 'External Secrets Operator', 'dev/test token secrets', 'PostgreSQL openbao databases'],
    monitorSignals: ['OpenBao pod readiness', 'external secret sync errors', 'token secret presence', 'seal state', 'auth delegator binding'],
    operatorQuestions: [
      'Can dev/test workloads resolve secrets and PKI material?',
      'Is OpenBao sealed, unreachable, misconfigured for Kubernetes auth, or missing token secrets?',
      'Are application failures caused by secret sync rather than the app itself?',
    ],
    firstResponse: [
      'Check OpenBao pod readiness and service reachability.',
      'Inspect ExternalSecret and ClusterSecretStore conditions.',
      'Check auth delegator binding before changing application secrets.',
      'Confirm whether failures affect dev, test, or both.',
    ],
    failureModes: [
      'OpenBao pod is running but sealed or unable to serve secret reads.',
      'ClusterSecretStore token secret is missing or stale.',
      'Kubernetes auth delegator binding collision prevents token reviews.',
      'ExternalSecrets sync errors surface as missing application credentials.',
    ],
    probes: [
      { label: 'OpenBao service up', kind: 'promql', target: 'up{namespace=~"dev-openbao|test-openbao"}', expectation: 'OpenBao scrape/service target is up.' },
      { label: 'ExternalSecret sync', kind: 'kubernetes', target: 'externalsecrets.external-secrets.io', expectation: 'No sustained sync errors in dev/test.' },
      { label: 'ClusterSecretStore readiness', kind: 'kubernetes', target: 'clustersecretstores.external-secrets.io', expectation: 'Dev/test OpenBao stores are Ready.' },
    ],
    telemetry: [
      { label: 'Pod readiness', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Kubernetes-level OpenBao health is available.' },
      { label: 'Secret sync conditions', state: 'planned', source: 'external-secrets metrics', description: 'Needed for precise operator view of secret propagation health.' },
      { label: 'Seal state', state: 'planned', source: 'OpenBao metrics or probe', description: 'Needed to distinguish running pod from usable secret backend.' },
    ],
    analysisQueries: [
      { label: 'OpenBao up', kind: 'promql', query: 'max(up{namespace=~"dev-openbao|test-openbao"} or up{job=~".*openbao.*"})', intent: 'Checks OpenBao scrape/service availability.' },
      { label: 'OpenBao pods', kind: 'kubectl', query: "kubectl get pods -A | rg '^(dev-openbao|test-openbao)\\s'", intent: 'Inspect OpenBao pod readiness in dev/test namespaces.' },
      { label: 'Secret store readiness', kind: 'kubectl', query: 'kubectl get clustersecretstore dev-openbao test-openbao -o wide', intent: 'Verify ExternalSecrets can authenticate to OpenBao.' },
    ],
    healthMetrics: namespaceHealthMetrics('openbao-dev-test', 'dev-openbao|test-openbao'),
    sourceRefs: [
      { label: 'Dev/test OpenBao applications', path: 'Agion-UpCloud-GitOps/argocd/applications/test-infrastructure' },
      { label: 'Dev/test ClusterSecretStores', path: 'Agion-UpCloud-GitOps/infrastructure/external-secrets' },
    ],
    promql: 'max(up{namespace=~"dev-openbao|test-openbao"} or up{job=~".*openbao.*"})',
    healthyWhen: 'positive',
  },
  {
    id: 'openbao-prod-beta',
    name: 'Prod/Beta OpenBao',
    environment: 'prod-beta',
    kind: 'secrets',
    criticality: 'critical',
    description: 'OpenBao secret backends for production and beta ExternalSecrets, PKI, application credentials, and pull credentials.',
    owner: 'GitOps OpenBao Helm release and external-secrets ClusterSecretStores',
    runtime: 'Kubernetes services openbao.prod-openbao.svc.cluster.local and openbao.beta-openbao.svc.cluster.local',
    dependencies: ['OpenBao pod readiness', 'External Secrets Operator', 'prod/beta token secrets', 'PostgreSQL openbao databases'],
    monitorSignals: ['OpenBao pod readiness', 'external secret sync errors', 'token secret presence', 'seal state', 'auth delegator binding'],
    operatorQuestions: [
      'Can production and beta workloads resolve secrets and PKI material?',
      'Are runtime failures actually secret-sync failures?',
      'Is the issue isolated to prod or beta, or caused by shared auth/delegator configuration?',
    ],
    firstResponse: [
      'Check prod/beta OpenBao pod readiness and service availability.',
      'Inspect ExternalSecret and ClusterSecretStore conditions for affected namespaces.',
      'Verify auth delegator binding before rotating tokens or app credentials.',
      'Correlate application rollout failures with secret sync events.',
    ],
    failureModes: [
      'OpenBao pod is running but sealed or unable to serve secret reads.',
      'ClusterSecretStore token secret is missing, stale, or in the wrong namespace.',
      'Kubernetes auth delegator binding collision breaks token review auth.',
      'ExternalSecrets sync failures cascade into app startup or image pull failures.',
    ],
    probes: [
      { label: 'OpenBao service up', kind: 'promql', target: 'up{namespace=~"prod-openbao|beta-openbao"}', expectation: 'OpenBao scrape/service target is up.' },
      { label: 'ExternalSecret sync', kind: 'kubernetes', target: 'externalsecrets.external-secrets.io', expectation: 'No sustained sync errors in prod/beta.' },
      { label: 'ClusterSecretStore readiness', kind: 'kubernetes', target: 'clustersecretstores.external-secrets.io', expectation: 'Prod/beta OpenBao stores are Ready.' },
    ],
    telemetry: [
      { label: 'Pod readiness', state: 'available', source: 'kube-state-metrics / OTel collector', description: 'Kubernetes-level OpenBao health is available.' },
      { label: 'Secret sync conditions', state: 'planned', source: 'external-secrets metrics', description: 'Needed for precise operator view of secret propagation health.' },
      { label: 'Seal state', state: 'planned', source: 'OpenBao metrics or probe', description: 'Needed to distinguish running pod from usable secret backend.' },
    ],
    analysisQueries: [
      { label: 'OpenBao up', kind: 'promql', query: 'max(up{namespace=~"prod-openbao|beta-openbao"} or up{job=~".*openbao.*"})', intent: 'Checks OpenBao scrape/service availability.' },
      { label: 'OpenBao pods', kind: 'kubectl', query: "kubectl get pods -A | rg '^(prod-openbao|beta-openbao)\\s'", intent: 'Inspect OpenBao pod readiness in prod/beta namespaces.' },
      { label: 'Secret store readiness', kind: 'kubectl', query: 'kubectl get clustersecretstore prod-openbao beta-openbao -o wide', intent: 'Verify ExternalSecrets can authenticate to OpenBao.' },
    ],
    healthMetrics: namespaceHealthMetrics('openbao-prod-beta', 'prod-openbao|beta-openbao'),
    sourceRefs: [
      { label: 'Prod/beta OpenBao applications', path: 'Agion-UpCloud-GitOps/argocd/applications/prod-infrastructure' },
      { label: 'Prod/beta ClusterSecretStores', path: 'Agion-UpCloud-GitOps/infrastructure/prod-external-secrets' },
    ],
    promql: 'max(up{namespace=~"prod-openbao|beta-openbao"} or up{job=~".*openbao.*"})',
    healthyWhen: 'positive',
  },
]

export const infraAssetKinds: InfraAssetKind[] = ['dedicated-vm', 'node-group', 'kubernetes-service', 'registry', 'secrets', 'managed-data']

export const infraAssetEnvironments: InfraAssetEnvironment[] = ['dev', 'test', 'beta', 'prod', 'prod-beta', 'shared']
